// React imports
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// MUI material imports
import { Alert, Box, Card, Chip, CircularProgress, Divider, Grid, IconButton, Snackbar, Tooltip,} from "@mui/material";

// MUI Icons imports
import { Add as AddIcon, Close, Delete, Edit, Email, Save,} from "@mui/icons-material";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import InlineEditField from "components/CustonEditableInlineField/CustomEditableInlineField";
import StatusBadge from "components/StatusBadge";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import SendMerchantAllocationEmailModal from "components/SendMerchantAllocationEmailModal";
import { TravelDetailSection, } from "components/travel/TravelDetailSection";

// Other imports
import { useAuth } from "auth/Auth";
import { usePermission } from "auth/usePermission";
import { formatDate } from "utils/DateTimeConversion";
import { commonIconButtonStyles, commonIconStyles } from "assets/theme/components/iconButtonStyles";
import { UiLabeledValue } from "components/v2/record/DetailSection";

const LoadingIconButton = ({ loading, onClick, icon, title, disabled, sx = {} }) => (
    <Tooltip title={title} placement="top">
        <span>
            <IconButton
                onClick={!disabled ? onClick : undefined}
                disabled={loading || disabled}
                sx={{
                    ...commonIconButtonStyles,
                    position: "relative",
                    ...(disabled && {
                        backgroundColor: "#e0e0e0 !important",
                        color: "#9e9e9e !important",
                    }),
                    "&.Mui-disabled": {
                        backgroundColor: "#e0e0e0 !important",
                        color: "#9e9e9e !important",
                        cursor: "not-allowed",
                    },
                    ...sx,
                }}
            >
                {loading ? (
                    <CircularProgress
                        size={24}
                        sx={{
                            color: "#ffffff",
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                        }}
                    />
                ) : (
                    icon
                )}
            </IconButton>
        </span>
    </Tooltip>
);

const formatMoney = (currency, amount) => {
    if (amount == null || amount === "") return "-";
    const cur = currency ? `${currency} ` : "";
    return `${cur}${amount}`;
};

const formatDateRange = (from, to) => {
    const start = from ? formatDate(from, "date") : null;
    const end = to ? formatDate(to, "date") : null;
    if (start && end) return `${start} → ${end}`;
    return start || end || "-";
};

const ClickableField = ({ label, value, onClick }) => {
    const display = value || "-";
    const canClick = Boolean(onClick && value);
    return (
        <MDBox>
            <MDTypography variant="button" fontWeight="regular" color="text">
                <strong>{label}:</strong>
            </MDTypography>
            <MDTypography
                component="span"
                variant="button"
                color={canClick ? "info" : "text"}
                fontWeight={canClick ? "medium" : "regular"}
                sx={{ cursor: canClick ? "pointer" : "default", ml: 1 }}
                onClick={canClick ? onClick : undefined}
            >
                {display}
            </MDTypography>
        </MDBox>
    );
};

const SidebarCard = ({ title, count, actionLabel, onAction, children, emptyMessage }) => (
    <Card>
        <MDBox
            mx={2}
            mt={-3}
            py={2}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
        >
            <MDTypography variant="h6" color="white" noWrap>
                {title}{count != null ? ` (${count})` : ""}
            </MDTypography>
            {actionLabel && onAction ? (
                <MDButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAction}
                >
                    {actionLabel}
                </MDButton>
            ) : null}
        </MDBox>
        <MDBox p={2}>
            {children || (
                <MDTypography variant="body2" color="text" textAlign="center" py={2}>
                    {emptyMessage || "No records."}
                </MDTypography>
            )}
        </MDBox>
    </Card>
);

const MerchantAllocationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const { canEdit, canDelete } = usePermission('merchant_allocations');

    const [allocation, setAllocation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const fetchAllocationData = async () => {
        setIsLoading(true);
        setIsUnauthorized(false);
        try {
            const response = await axiosInstance.get(`/merchant-allocations/${id}`);
            setAllocation(response.data);
        } catch (error) {
            console.error("Error fetching allocation data:", error);
            if (error.response?.status === 403) {
                setIsUnauthorized(true);
                setAllocation(null);
                setSnackbar({
                    open: true,
                    message: "You don't have permission to access this Merchant Allocation",
                    severity: "error",
                });
                setTimeout(() => navigate("/merchant-allocations"), 3000);
            } else {
                setSnackbar({
                    open: true,
                    message: "Error loading allocation details",
                    severity: "error",
                });
                setAllocation(null);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllocationData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields((prev) => {
            const next = { ...prev, [field]: value };
            if (field === "pricing_model" && value !== "Dated") {
                next.season_from = null;
                next.season_to = null;
            }
            return next;
        });
        setAllocation((prev) => {
            const next = { ...prev, [field]: value };
            if (field === "pricing_model" && value !== "Dated") {
                next.season_from = null;
                next.season_to = null;
            }
            // Optimistic badge only — server resets status on merchant_changed.
            // Do not PUT status: reverting merchant_id must not force Pending onto
            // an Accepted row that never actually changed merchant on save.
            if (field === "merchant_id") {
                const prevMerchant = prev?.merchant_id;
                if (
                    prevMerchant != null
                    && prevMerchant !== ""
                    && String(prevMerchant) !== String(value)
                ) {
                    next.status = "Pending";
                    next.rejection_reason = null;
                    next.rejected_on = null;
                }
            }
            return next;
        });
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/merchant-allocations/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({
                open: true,
                message: "Allocation updated successfully",
                severity: "success",
            });
            fetchAllocationData();
        } catch (error) {
            console.error("Error updating allocation:", error);
            const detail = error?.response?.data?.detail;
            setSnackbar({
                open: true,
                message: error.response?.status === 403
                    ? "You don't have permission to update this allocation"
                    : (typeof detail === "string" ? detail : "Error updating allocation"),
                severity: "error",
            });
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setUpdatedFields({});
        fetchAllocationData();
    };

    const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);
    const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

    const handleConfirmDelete = async () => {
        try {
            await axiosInstance.delete(`/merchant-allocations/${id}`);
            setSnackbar({
                open: true,
                message: "Allocation deleted successfully",
                severity: "success",
            });
            setTimeout(() => navigate("/merchant-allocations"), 800);
        } catch (error) {
            console.error("Error deleting allocation:", error);
            setSnackbar({
                open: true,
                message: error.response?.status === 403
                    ? "You don't have permission to delete this allocation"
                    : "Error deleting allocation",
                severity: "error",
            });
            throw error;
        }
    };

    const handleOpenEmailModal = () => {
        if ((allocation?.status || "Pending") !== "Pending") {
            setSnackbar({
                open: true,
                message: "Only Pending allocations can send the request email.",
                severity: "warning",
            });
            return;
        }
        setIsEmailModalOpen(true);
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({
                open: true,
                message: `Unable to find user info for ${userName || "this user"}.`,
                severity: "info",
            });
        }
    };

    const handleEditInWizard = () => {
        const tripId = Number(allocation?.trip_id);
        const packageId = Number(allocation?.trip_package_id);
        if (Number.isInteger(tripId) && tripId > 0) {
            const base = Number.isInteger(packageId) && packageId > 0
                ? `/trips/${tripId}/packages/${packageId}/edit`
                : `/trips/${tripId}/edit`;
            navigate(`${base}?step=allocations`);
            return;
        }
        setIsEditing(true);
    };

    const assets = allocation?.asset_inventory || allocation?.related?.merchant_assets || [];
    const orderLines = allocation?.related?.merchant_order_lines || [];

    const renderSection = (title, fields) => (
        <TravelDetailSection title={title}>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={item.fullWidth ? 12 : 6} key={item.field}>
                        {["created_by_name", "updated_by_name"].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() =>
                                        handleUserClick(
                                            item.field === "created_by_name"
                                                ? allocation.created_by
                                                : allocation.updated_by,
                                            allocation[item.field]
                                        )
                                    }
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: "pointer", ml: 1 }}
                                >
                                    {allocation[item.field] || "-"}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : item.custom ? (
                            item.custom(allocation)
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={allocation?.[item.field]}
                                options={item.options || null}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing && !item.readOnly}
                                readOnly={item.readOnly || false}
                                renderValue={
                                    item.renderValue
                                    || ((val) => (val === 0 || val ? String(val) : "-"))
                                }
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </TravelDetailSection>
    );

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox py={6} display="flex" justifyContent="center">
                    <DataLoadingSpinner />
                </MDBox>
            </DashboardLayout>
        );
    }

    if (!allocation) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>
                        {isUnauthorized
                            ? "You don't have permission to access this Merchant Allocation"
                            : "Allocation not found"}
                    </MDTypography>
                    <MDButton
                        sx={{ mt: 2 }}
                        variant="outlined"
                        color="info"
                        onClick={() => navigate("/merchant-allocations")}
                    >
                        Back to list
                    </MDButton>
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                gap={2}
                                flexWrap="wrap"
                            >
                                <MDBox sx={{ minWidth: 0, flex: 1 }}>
                                    <MDTypography variant="h5" color="white" noWrap>
                                        {allocation.name || `Allocation #${allocation.id}`}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.85} display="block">
                                        Allocation #{allocation.id}
                                        {allocation.merchant_name ? ` · ${allocation.merchant_name}` : ""}
                                    </MDTypography>
                                    <MDTypography variant="caption" color="white" opacity={0.8} display="block" mt={0.5}>
                                        Category: {allocation.merchant_category || allocation.component_category || "—"}
                                        {allocation.merchant_partner_code
                                            ? ` · Code: ${allocation.merchant_partner_code}`
                                            : ""}
                                    </MDTypography>
                                </MDBox>

                                <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                    {canEdit && (allocation?.status || "Pending") === "Pending" && (
                                        <LoadingIconButton
                                            loading={false}
                                            onClick={handleOpenEmailModal}
                                            icon={<Email sx={commonIconStyles} />}
                                            title="Send allocation request email"
                                        />
                                    )}
                                    {canEdit && (isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton onClick={handleCancelEdit} sx={commonIconButtonStyles}>
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton onClick={handleSaveChanges} sx={commonIconButtonStyles}>
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Allocation">
                                            <IconButton onClick={handleEditInWizard} sx={commonIconButtonStyles}>
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    ))}
                                    {canDelete && (
                                        <Tooltip title="Delete Allocation">
                                            <IconButton onClick={handleOpenDeleteModal} sx={commonIconButtonStyles}>
                                                <Delete sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </MDBox>
                            </MDBox>

                            <MDBox p={3}>
                                {renderSection("Allocation Overview", [
                                    { label: "Allocation Name", field: "name" },
                                    {
                                        label: "Status",
                                        field: "status",
                                        options: [
                                            { value: "Pending", label: "Pending" },
                                            { value: "Accepted", label: "Accepted" },
                                            { value: "Rejected", label: "Rejected" },
                                        ],
                                        custom: (data) =>
                                            isEditing ? (
                                                <InlineEditField
                                                    label="Status"
                                                    value={data?.status}
                                                    options={[
                                                        { value: "Pending", label: "Pending" },
                                                        { value: "Accepted", label: "Accepted" },
                                                        { value: "Rejected", label: "Rejected" },
                                                    ]}
                                                    onSave={(v) => handleFieldUpdate("status", v)}
                                                    isEditing
                                                />
                                            ) : (
                                                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                    <MDTypography variant="button" fontWeight="regular" color="text">
                                                        <strong>Status:</strong>
                                                    </MDTypography>
                                                    <StatusBadge status={data?.status || "Pending"} />
                                                </MDBox>
                                            ),
                                    },
                                    {
                                        label: "Merchant",
                                        field: "merchant_name",
                                        readOnly: true,
                                        custom: () => (
                                            <ClickableField
                                                label="Merchant"
                                                value={allocation.merchant_name}
                                                onClick={
                                                    allocation.merchant_id
                                                        ? () => navigate(`/accounts/${allocation.merchant_id}`)
                                                        : null
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: "Trip Logistics",
                                        field: "component_name",
                                        readOnly: true,
                                        custom: () => (
                                            <ClickableField
                                                label="Trip Logistics"
                                                value={allocation.component_name}
                                                onClick={
                                                    allocation.package_component_id
                                                        ? () =>
                                                            navigate(
                                                                `/trip-package-components/${allocation.package_component_id}`
                                                            )
                                                        : null
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: "Package",
                                        field: "package_title",
                                        readOnly: true,
                                        custom: () => (
                                            <ClickableField
                                                label="Package"
                                                value={allocation.package_title}
                                                onClick={
                                                    allocation.trip_package_id
                                                        ? () =>
                                                            navigate(
                                                                `/trip-packages/detail/${allocation.trip_package_id}`
                                                            )
                                                        : null
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: "Destination",
                                        field: "trip_title",
                                        readOnly: true,
                                        custom: () => (
                                            <ClickableField
                                                label="Destination"
                                                value={allocation.trip_title}
                                                onClick={
                                                    allocation.trip_id
                                                        ? () => navigate(`/trips/detail/${allocation.trip_id}`)
                                                        : null
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: "Departure",
                                        field: "departure_date",
                                        readOnly: true,
                                        custom: () => (
                                            <ClickableField
                                                label="Departure"
                                                value={
                                                    allocation.departure_date
                                                        ? formatDateRange(
                                                            allocation.departure_date,
                                                            allocation.return_date
                                                        )
                                                        : null
                                                }
                                                onClick={
                                                    allocation.departure_date_id
                                                        ? () =>
                                                            navigate(
                                                                `/trip-departure-dates/${allocation.departure_date_id}`
                                                            )
                                                        : null
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: "Departure City / Route",
                                        field: "departure_city",
                                        readOnly: true,
                                    },
                                    {
                                        label: "Active",
                                        field: "is_active",
                                        options: [
                                            { value: true, label: "Yes" },
                                            { value: false, label: "No" },
                                        ],
                                        renderValue: (val) => (val ? "Yes" : "No"),
                                    },
                                    {
                                        label: "Allocation Priority",
                                        field: "allocation_priority",
                                        renderValue: (val) => (val == null ? "0" : String(val)),
                                    },
                                    {
                                        label: "Request Email Status",
                                        field: "request_email_status",
                                        readOnly: true,
                                        renderValue: (val) => {
                                            if (!val) return "Not sent";
                                            const err = allocation?.request_email_error;
                                            if (val === "failed" && err) return `Failed — ${err}`;
                                            return String(val).charAt(0).toUpperCase() + String(val).slice(1);
                                        },
                                    },
                                    {
                                        label: "Request Email Sent At",
                                        field: "request_email_sent_at",
                                        readOnly: true,
                                        renderValue: (val) => (val ? formatDate(val, "datetime") : "-"),
                                    },
                                    {
                                        label: "Request Expires At",
                                        field: "request_expires_at",
                                        readOnly: true,
                                        renderValue: (val) => (val ? formatDate(val, "datetime") : "-"),
                                    },
                                ])}

                                {renderSection("Merchant Basic Details", [
                                    {
                                        label: "Merchant",
                                        field: "merchant_name",
                                        readOnly: true,
                                        custom: () => (
                                            <ClickableField
                                                label="Merchant"
                                                value={allocation.merchant_name}
                                                onClick={
                                                    allocation.merchant_id
                                                        ? () => navigate(`/accounts/${allocation.merchant_id}`)
                                                        : null
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: "Primary Contact",
                                        field: "primary_contact_name",
                                        readOnly: true,
                                        custom: () => (
                                            <ClickableField
                                                label="Primary Contact"
                                                value={allocation.primary_contact_name || "-"}
                                                onClick={
                                                    allocation.primary_contact_id
                                                        ? () =>
                                                            navigate(`/contacts/${allocation.primary_contact_id}`)
                                                        : null
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: "Operations Designation",
                                        field: "merchant_partner_type",
                                        readOnly: true,
                                        renderValue: () =>
                                            allocation.merchant_partner_type
                                            || allocation.merchant_category
                                            || "-",
                                    },
                                    {
                                        label: "Phone",
                                        field: "merchant_phone",
                                        readOnly: true,
                                        renderValue: () => allocation.merchant_phone || "-",
                                    },
                                    {
                                        label: "Email",
                                        field: "merchant_email",
                                        readOnly: true,
                                        renderValue: () => allocation.merchant_email || "-",
                                    },
                                    {
                                        label: "Compliance ID (Tax / VAT)",
                                        field: "merchant_tax_id",
                                        readOnly: true,
                                        renderValue: () => allocation.merchant_tax_id || "-",
                                    },
                                    {
                                        label: "HQ Address",
                                        field: "merchant_hq_address",
                                        readOnly: true,
                                        fullWidth: true,
                                        renderValue: () => allocation.merchant_hq_address || "-",
                                    },
                                ])}

                                {renderSection("Contracted Rates & Pricing", [
                                    {
                                        label: "Pricing Model",
                                        field: "pricing_model",
                                        options: [
                                            { value: "Dated", label: "Dated" },
                                            { value: "Tiered", label: "Tiered" },
                                            { value: "PerPax", label: "PerPax" },
                                            { value: "PerRoom", label: "PerRoom" },
                                        ],
                                    },
                                    { label: "Currency", field: "currency" },
                                    {
                                        label: "Merchant Cost",
                                        field: "cost",
                                        renderValue: (val) => formatMoney(allocation.currency, val),
                                    },
                                    ...(allocation.pricing_model === "Dated"
                                        ? [
                                            {
                                                label: "Date From",
                                                field: "season_from",
                                                renderValue: (val) =>
                                                    val ? formatDate(val, "date") : "-",
                                            },
                                            {
                                                label: "Date To",
                                                field: "season_to",
                                                renderValue: (val) =>
                                                    val ? formatDate(val, "date") : "-",
                                            },
                                        ]
                                        : []),
                                    {
                                        label: "Requested Allocation",
                                        field: "min_number",
                                        renderValue: (val) => (val == null ? "-" : String(val)),
                                    },
                                ])}

                                {renderSection("Rejection Details", [
                                    { label: "Rejection Reason", field: "rejection_reason" },
                                    {
                                        label: "Rejected On",
                                        field: "rejected_on",
                                        renderValue: (val) => (val ? formatDate(val, "date") : "-"),
                                    },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    {
                                        label: "Created Date",
                                        field: "created_at",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, "datetime"),
                                    },
                                    {
                                        label: "Modified Date",
                                        field: "updated_at",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, "datetime"),
                                    },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>

                        <Box mt={5} />
                        <SidebarCard
                            title="Merchant Assets"
                            count={assets.length}
                            actionLabel="New"
                            onAction={() => navigate("/merchant-assets")}
                            emptyMessage="No assets for this merchant."
                        >
                            {assets.length > 0 ? (
                                assets.map((asset, index) => (
                                    <Box key={asset.id || index}>
                                        {index > 0 && <Divider sx={{ my: 1.25 }} />}
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => navigate(`/merchant-assets/${asset.id}`)}
                                        >
                                            <MDBox>
                                                <MDTypography variant="button" fontWeight="medium">
                                                    {asset.name}
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text" display="block">
                                                    {asset.asset_type || "Asset"}
                                                    {asset.capacity_per_slot != null
                                                        ? ` · Capacity ${asset.capacity_per_slot}`
                                                        : ""}
                                                </MDTypography>
                                            </MDBox>
                                            {asset.status ? (
                                                <Chip label={asset.status} size="small" />
                                            ) : null}
                                        </MDBox>
                                    </Box>
                                ))
                            ) : null}
                        </SidebarCard>

                        <Box mt={5} />
                        <SidebarCard
                            title="Merchant Order Lines"
                            count={orderLines.length}
                            emptyMessage="No merchant order lines linked to this allocation."
                        >
                            {orderLines.length > 0 ? (
                                orderLines.map((line, index) => (
                                    <Box key={line.id || index}>
                                        {index > 0 && <Divider sx={{ my: 1.25 }} />}
                                        <MDBox
                                            sx={{ cursor: line.id ? "pointer" : "default" }}
                                            onClick={() => {
                                                if (line.id) navigate(`/merchant-order-line/${line.id}`);
                                            }}
                                        >
                                            <MDTypography variant="button" fontWeight="medium">
                                                {line.name || `Order line #${line.id}`}
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                {line.pax_count != null ? `${line.pax_count} pax` : ""}
                                                {line.status ? ` · ${line.status}` : ""}
                                            </MDTypography>
                                        </MDBox>
                                    </Box>
                                ))
                            ) : null}
                        </SidebarCard>

                        {allocation.trip_id ? (
                            <>
                                <Box mt={5} />
                                <SidebarCard title="Destination & Logistics">
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => navigate(`/trips/detail/${allocation.trip_id}`)}
                                    >
                                        <MDBox>
                                            <MDTypography variant="button" fontWeight="medium">
                                                {allocation.trip_title || `Trip #${allocation.trip_id}`}
                                            </MDTypography>
                                            {allocation.package_title ? (
                                                <MDTypography variant="caption" color="text" display="block">
                                                    {allocation.package_title}
                                                </MDTypography>
                                            ) : null}
                                        </MDBox>
                                    </MDBox>
                                    {allocation.package_component_id ? (
                                        <>
                                            <Divider sx={{ my: 1.5 }} />
                                            <MDBox
                                                sx={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    navigate(`/trip-package-components/${allocation.package_component_id}`)
                                                }
                                            >
                                                <MDTypography variant="button" fontWeight="medium" color="info">
                                                    {allocation.component_name || "Trip Logistics"}
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text" display="block">
                                                    View trip logistics
                                                </MDTypography>
                                            </MDBox>
                                        </>
                                    ) : null}
                                </SidebarCard>
                            </>
                        ) : null}
                    </Grid>
                </Grid>
            </MDBox>

            <ConfirmDeleteModal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
                title="Delete Merchant Allocation"
                message="Are you sure you want to delete this allocation? This action cannot be undone."
            />

            <SendMerchantAllocationEmailModal
                open={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                allocationId={id}
                allocationName={allocation?.name}
                onSent={() => {
                    fetchAllocationData();
                    setSnackbar({
                        open: true,
                        message: "Allocation request email sent",
                        severity: "success",
                    });
                }}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default MerchantAllocationDetail;
